import { useMemo, useState } from 'react';
import { tokens, Text, TextInput, Button, Stack, Loader } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import type { UserAttributeOptionType, UserAttributeType } from './UserAttributeFilterType';
import { GenericFilterDropdown } from '../GenericBloc/GenericFilter/GenericFilterDropdown';
import { getIconName } from './UserAttributeUtils';

const Footer = styled.div`
  padding: ${tokens.spacer.x2};
  border-top: 1px solid ${tokens.color.ui_border_light};
`;

const TextInputContainer = styled.div`
  padding: ${tokens.spacer.x2};
  border-bottom: 1px solid ${tokens.color.ui_border_light};
`;

const NoResultDiv = styled.div`
  text-align: center;
  padding: ${tokens.spacer.x2};
`;

const LoaderContainer = styled.div`
  padding: ${tokens.spacer.x2};
`;

const StyledButton = styled(Button)`
  ${tokens.text('body2')};
  ${tokens.truncateText()};
  display: inline-block;
  text-align: left;
  min-height: ${tokens.spacer.x4};
  border-radius: 0;
`;

const StyledStack = styled(Stack)`
  max-height: 300px;
  overflow-y: scroll;
`;

interface UserAttributeSelectorProps {
  filterName: string;
  userAttributeOptionList: UserAttributeOptionType[];
  userAttributeOptionIsLoading: boolean;
  onUserAttributeSelected: (newUserAttribute: UserAttributeType) => void;
  onAddMoreUserAttribute?: () => void;
}

export function UserAttributeSelector({
  filterName,
  userAttributeOptionList,
  userAttributeOptionIsLoading,
  onUserAttributeSelected,
  onAddMoreUserAttribute,
}: UserAttributeSelectorProps): JSX.Element {
  const [searchInput, setSearchInput] = useState('');
  const filtereduserAttributeOptionList = useMemo(() => {
    if (userAttributeOptionIsLoading) {
      return [];
    }
    if (searchInput) {
      return userAttributeOptionList.filter((item) =>
        item.name.toLocaleLowerCase().includes(searchInput),
      );
    }
    return userAttributeOptionList;
  }, [searchInput, userAttributeOptionList, userAttributeOptionIsLoading]);

  return (
    <GenericFilterDropdown ariaLabel={`${filterName} dropdown`}>
      {userAttributeOptionIsLoading && (
        <LoaderContainer>
          <Loader size="l" isGreyscale />
        </LoaderContainer>
      )}
      {!userAttributeOptionIsLoading && (
        <>
          <TextInputContainer>
            <TextInput
              id="searchInput"
              name="Search attributes"
              icon="user_attribute"
              placeholder="Search user attributes..."
              size="m"
              defaultValue={searchInput}
              onChange={(event) => setSearchInput(event.target.value.toLowerCase().trim())}
              autoComplete="off"
            />
          </TextInputContainer>
          {filtereduserAttributeOptionList.length > 0 && (
            <StyledStack direction="column" spacing="x0">
              {filtereduserAttributeOptionList.map((item) => (
                <StyledButton
                  kind="ghost"
                  size="s"
                  key={item.name}
                  icon={getIconName(item.kind)}
                  onClick={() => onUserAttributeSelected(item)}
                  title={item.name}
                >
                  {item.name}
                </StyledButton>
              ))}
            </StyledStack>
          )}
          {filtereduserAttributeOptionList.length === 0 && (
            <NoResultDiv>
              <Text textType="body2">No user attribute match</Text>
            </NoResultDiv>
          )}
        </>
      )}
      {onAddMoreUserAttribute && (
        <Footer>
          <Button size="s" kind="secondary" onClick={onAddMoreUserAttribute}>
            Add more attributes
          </Button>
        </Footer>
      )}
    </GenericFilterDropdown>
  );
}
